import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { SectionCard } from 'src/components/shared/SectionCard'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { useInternalPermissions } from 'src/hooks/useInternalPermissions'
import type { InternalAccessRole, InternalRoleFormValues } from 'src/interfaces/internalAccess'
import { getInternalRoleSaving } from 'src/selectors/internalAccess'
import {
  buildRoleUpdatePayload,
  resolveRoleEditability,
} from 'src/utils/internalAccess/internalRoles'
import { InternalPermissionsField } from './InternalPermissionsField'
import { internalRoleFormValidator } from './InternalRoleForm.validator'

const FORM_ID = 'internal-role-form'

interface Props {
  /** `null` in create mode. */
  role: InternalAccessRole | null
  onSubmit(values: InternalRoleFormValues): void
  onCancel(): void
}

/**
 * `enableReinitialize`: after a save the reducer swaps in the returned role, and the form must
 * reset to it so "nothing changed" (Save disabled) holds again.
 */
const InternalRoleFormComponent = ({ role, onSubmit, onCancel }: Props) => {
  const canManage = useHasPermission(InternalPermission.InternalRolesManage)
  const isSaving = useSelector(getInternalRoleSaving)
  const grantable = useInternalPermissions()

  const editability = useMemo(() => {
    return resolveRoleEditability({ role, canManage, grantable })
  }, [role, canManage, grantable])

  // Copied: Formik owns its values, and `role.permissions` is a reference into the store.
  const initialValues = useMemo<InternalRoleFormValues>(() => {
    return { name: role?.name ?? '', permissions: role ? [...role.permissions] : [] }
  }, [role])

  return (
    <Formik<InternalRoleFormValues>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={internalRoleFormValidator}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue }) => {
        const hasChanges = role ? buildRoleUpdatePayload(role, values) !== null : true

        return (
          <Form id={FORM_ID} css={tw`flex flex-col gap-6`} data-testid="internal-role-form">
            {editability.lockReason ? (
              <SectionCard title="Read-only">
                <Typography variant="body2" css={tw`text-shades-700`}>
                  {editability.lockReason}
                </Typography>
              </SectionCard>
            ) : null}
            <SectionCard title="Details">
              <div css={tw`flex w-full max-w-md flex-col gap-2`}>
                <FormikTextField<InternalRoleFormValues>
                  name="name"
                  label="Role name"
                  placeholder="e.g. support_lead"
                  fullWidth
                  disabled={!editability.canEditName || isSaving}
                  data-testid="internal-role-name-input"
                />
                {editability.nameHelperText ? (
                  <Typography variant="body2" css={tw`text-xs text-shades-500`}>
                    {editability.nameHelperText}
                  </Typography>
                ) : null}
              </div>
            </SectionCard>
            <SectionCard title="Permissions">
              {role?.allPermissions ? (
                <Typography variant="body2" css={tw`text-shades-700`}>
                  Grants every permission, including ones added later.
                </Typography>
              ) : (
                <div css={tw`flex flex-col gap-2`}>
                  {editability.permissionsHelperText ? (
                    <Typography variant="body2" css={tw`text-xs text-shades-500`}>
                      {editability.permissionsHelperText}
                    </Typography>
                  ) : null}
                  <InternalPermissionsField
                    value={values.permissions}
                    rolePermissions={role?.permissions ?? values.permissions}
                    disabled={!editability.canEditPermissions || isSaving}
                    onChange={(permissions) => {
                      setFieldValue('permissions', permissions)
                    }}
                  />
                </div>
              )}
            </SectionCard>
            {editability.canSave ? (
              <div css={tw`flex flex-row justify-end gap-2`}>
                <Button
                  size="small"
                  color="grey"
                  onClick={onCancel}
                  data-testid="internal-role-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="dark"
                  type="submit"
                  form={FORM_ID}
                  loading={isSaving}
                  disabled={isSaving || !hasChanges}
                  data-testid="internal-role-save-button"
                >
                  {role ? 'Save role' : 'Create role'}
                </Button>
              </div>
            ) : null}
          </Form>
        )
      }}
    </Formik>
  )
}

export const InternalRoleForm = memo(InternalRoleFormComponent)
